import { GRAVIDADE_M_S2 } from './estatica'

/**
 * Força de choque de uma queda retida por um talabarte (ou corda), didática e
 * em TypeScript puro: o número que a animação e a física vão conferir.
 *
 * Modelo (queda na vertical, corpo como massa só, sem atrito nem amortecimento):
 *
 *   m   = massa (trabalhador com equipamento)
 *   h   = queda livre até o talabarte esticar
 *   L   = comprimento do talabarte
 *   k   = EA / L, a rigidez do talabarte (mola que só puxa)
 *
 * Sem absorvedor, a energia da queda vira toda deformação do talabarte:
 *
 *   m·g·(h + x) = k·x² / 2   →   F = m·g + √((m·g)² + 2·m·g·EA·f),  f = h / L
 *
 * A força depende do fator de queda, não da altura: o talabarte mais longo
 * cai mais, mas também estica mais.
 *
 * Com absorvedor, ele é um freio de força constante `Fa`: o talabarte estica
 * até `Fa`, o absorvedor rasga com a força parada em `Fa` até a energia
 * acabar, e só se ela não acabar dentro da abertura máxima (absorvedor
 * esgotado) o talabarte volta a esticar e a força sobe acima de `Fa`.
 */

export interface EntradaForcaDeChoque {
  readonly massaKg: number
  readonly quedaLivreM: number
  readonly comprimentoM: number
  readonly rigidezEAkN: number
  /** Força de frenagem do absorvedor. Ignorada se não há absorvedor. */
  readonly forcaAbsorvedorKN: number
  /** Abertura máxima do absorvedor; 0 é sem absorvedor. */
  readonly aberturaMaximaAbsorvedorM: number
}

export type RegimeChoque =
  /** Só o talabarte segura: a força sobe até a energia acabar. */
  | 'elastico'
  /** O absorvedor abre e segura a força em `Fa`. */
  | 'absorvedor'
  /** O absorvedor abre todo e a força volta a subir no talabarte. */
  | 'absorvedor-esgotado'

export interface ResultadoForcaDeChoque {
  readonly regime: RegimeChoque
  /** Pico de força no corpo, igual ao da ancoragem quando o talabarte vai direto a ela. */
  readonly forcaPicoKN: number
  /** O peso: a força que fica depois de a queda parar. */
  readonly pesoKN: number
  /** Quanto o talabarte estica no pico. */
  readonly alongamentoNoPicoM: number
  /** Quanto o absorvedor abriu. */
  readonly aberturaAbsorvedorM: number
  /** Do momento em que o talabarte estica até o ponto mais baixo: alongamento + abertura. */
  readonly descidaAposEsticarM: number
  /** Energia da queda até parar, em joules: m·g·(h + descida). */
  readonly energiaJ: number
  /** O freio do absorvedor é mais fraco que o peso: ele abre todo, qualquer que seja a queda. */
  readonly freioMaisFracoQueOPeso: boolean
  /** Quanto o talabarte fica esticado com o corpo parado, pendurado: m·g / k. */
  readonly alongamentoEstaticoM: number
}

const g = GRAVIDADE_M_S2

export function calcularForcaDeChoque(e: EntradaForcaDeChoque): ResultadoForcaDeChoque {
  const peso = e.massaKg * g // N
  const k = (e.rigidezEAkN * 1000) / e.comprimentoM // N/m
  const h = Math.max(0, e.quedaLivreM)
  const pesoKN = peso / 1000
  const resultado = (regime: RegimeChoque, forcaN: number, alongamentoM: number, aberturaM: number): ResultadoForcaDeChoque => ({
    regime,
    forcaPicoKN: forcaN / 1000,
    pesoKN,
    alongamentoNoPicoM: alongamentoM,
    aberturaAbsorvedorM: aberturaM,
    descidaAposEsticarM: alongamentoM + aberturaM,
    energiaJ: peso * (h + alongamentoM + aberturaM),
    freioMaisFracoQueOPeso: e.aberturaMaximaAbsorvedorM > 0 && e.forcaAbsorvedorKN * 1000 <= peso,
    alongamentoEstaticoM: peso / k,
  })

  // só o talabarte
  const xElastico = (peso + Math.sqrt(peso * peso + 2 * k * peso * h)) / k
  const forcaElastica = k * xElastico
  const Fa = e.forcaAbsorvedorKN * 1000
  const Dmax = e.aberturaMaximaAbsorvedorM
  if (Dmax <= 0 || forcaElastica <= Fa) return resultado('elastico', forcaElastica, xElastico, 0)
  // o talabarte estica até Fa; daí o absorvedor rasga com força constante
  const xa = Fa / k
  const energiaAoAbrir = peso * (h + xa) - (k * xa * xa) / 2 // energia cinética quando o absorvedor começa a abrir
  // freio mais fraco que o peso: a queda acelera enquanto ele abre, até abrir todo
  const abertura = Fa > peso ? energiaAoAbrir / (Fa - peso) : Infinity
  if (abertura <= Dmax) return resultado('absorvedor', Fa, xa, abertura)

  // abriu todo: o resto da energia volta para o talabarte, a partir de Fa
  const sobra = energiaAoAbrir - (Fa - peso) * Dmax
  const excesso = Fa - peso
  const y = (-excesso + Math.sqrt(excesso * excesso + 2 * k * sobra)) / k
  return resultado('absorvedor-esgotado', Fa + k * y, xa + y, Dmax)
}

// ---------------------------------------------------------------------------
// A força no tempo
// ---------------------------------------------------------------------------

export interface PontoCurvaForca {
  /** Tempo desde que o talabarte esticou. */
  readonly t: number
  readonly forcaKN: number
  /** Quanto o corpo desceu desde que o talabarte esticou: alongamento + abertura. */
  readonly descidaM: number
}

export interface CurvaDeForca {
  readonly pontos: readonly PontoCurvaForca[]
  /** Do talabarte esticar até o corpo parar no ponto mais baixo. */
  readonly duracaoS: number
  readonly picoKN: number
}

const PASSO_INTEGRACAO_S = 1e-5
const DURACAO_MAXIMA_CURVA_S = 5

/**
 * A força no corpo do instante em que o talabarte estica até o ponto mais
 * baixo, integrada passo a passo com o mesmo modelo de `calcularForcaDeChoque`
 * (massa, mola que só puxa, freio de força constante). É a conferência
 * independente do cálculo fechado e a curva do gráfico.
 */
export function curvaDeForca(e: EntradaForcaDeChoque, amostras = 120): CurvaDeForca {
  const peso = e.massaKg * g
  const k = (e.rigidezEAkN * 1000) / e.comprimentoM
  const Fa = e.forcaAbsorvedorKN * 1000
  const Dmax = e.aberturaMaximaAbsorvedorM
  const dt = PASSO_INTEGRACAO_S
  let v = Math.sqrt(2 * g * Math.max(0, e.quedaLivreM))
  let x = 0
  let d = 0
  let t = 0
  let pico = 0
  const brutos: PontoCurvaForca[] = [{ t: 0, forcaKN: 0, descidaM: 0 }]
  while (t < DURACAO_MAXIMA_CURVA_S) {
    const forcaMola = k * x
    const abrindo = Dmax > 0 && forcaMola >= Fa && d < Dmax
    const forca = abrindo ? Fa : forcaMola
    pico = Math.max(pico, forca)
    const vNovo = v + (g - forca / e.massaKg) * dt
    if (vNovo <= 0) {
      // para no meio do passo: guarda o ponto mais baixo, com a força do pico
      t += dt * (v / (v - vNovo))
      brutos.push({ t, forcaKN: forca / 1000, descidaM: x + d })
      break
    }
    v = vNovo
    if (abrindo) d = Math.min(Dmax, d + v * dt)
    else x += v * dt
    t += dt
    brutos.push({ t, forcaKN: forca / 1000, descidaM: x + d })
  }
  // amostra a curva, sempre com o primeiro, o pico e o último ponto
  const passo = Math.max(1, Math.floor(brutos.length / amostras))
  const indicePico = brutos.reduce((melhor, p, i) => (p.forcaKN > brutos[melhor]!.forcaKN ? i : melhor), 0)
  const pontos = brutos.filter((_, i) => i % passo === 0 || i === indicePico || i === brutos.length - 1)
  return { pontos, duracaoS: brutos[brutos.length - 1]!.t, picoKN: pico / 1000 }
}

/** A curva no instante `t` (desde o talabarte esticar), interpolada. */
export function curvaNoInstante(curva: CurvaDeForca, t: number): PontoCurvaForca {
  const p = curva.pontos
  if (t <= 0) return p[0]!
  if (t >= curva.duracaoS) return p[p.length - 1]!
  let i = 1
  while (i < p.length - 1 && p[i]!.t < t) i += 1
  const a = p[i - 1]!
  const b = p[i]!
  const u = b.t > a.t ? (t - a.t) / (b.t - a.t) : 1
  return { t, forcaKN: a.forcaKN + (b.forcaKN - a.forcaKN) * u, descidaM: a.descidaM + (b.descidaM - a.descidaM) * u }
}
